import {
  ConnectedOverlayPositionChange,
  ConnectedPosition,
  Overlay,
  OverlayModule,
} from '@angular/cdk/overlay';
import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, TemplateRef, inject } from '@angular/core';

import { isEqualWith } from 'lodash';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  map,
  scan,
  startWith,
} from 'rxjs';

import { AssetsModule, icons } from '@tsq-web/assets';

import { TableColumnActions } from '../../models/table.model';
import { compareConnectedPositions } from '../../utils/table';
import { TableCellBaseComponent } from '../table-cell-base/table-cell-base.component';

@Component({
  standalone: true,
  imports: [AsyncPipe, NgIf, NgTemplateOutlet, AssetsModule, OverlayModule, TableCellBaseComponent],
  selector: 'tsq-table-cell-actions',
  template: `
    <tsq-table-cell-base class="text-coal-secondary" size="small">
      <ng-container *ngIf="show$ | async">
        <button
          type="button"
          class="bg-petro-p1 hover:bg-petro-n2 hover:text-primary rounded transition-colors"
          cdkOverlayOrigin
          #overlayOrigin="cdkOverlayOrigin"
          (click)="toggleActionsCard(); $event.stopPropagation()"
        >
          <tsq-icon [icon]="dotsIcon" classes="rotate-90" />
        </button>

        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="overlayOrigin"
          [cdkConnectedOverlayOpen]="open$ | async"
          [cdkConnectedOverlayPositions]="overlayPositions"
          [cdkConnectedOverlayScrollStrategy]="overlayScrollStrategy"
          (overlayOutsideClick)="closeActionsCard()"
          (positionChange)="onPositionChange($event)"
          (detach)="closeActionsCard()"
        >
          <div
            class="border-1 bg-petro-p1 border-petro-n2 content rounded p-8 shadow"
            [attr.actions-card-arrow]="actionsCardArrow$ | async"
            (click)="closeActionsCard()"
          >
            <div class="bg-petro-p1 relative z-10">
              <ng-container
                *ngTemplateOutlet="actionsTemplate; context: { $implicit: rowData$ | async }"
              />
            </div>
          </div>
        </ng-template>
      </ng-container>
    </tsq-table-cell-base>
  `,
  styles: [
    `
      button {
        &:focus-visible {
          outline: theme('colors.purple.DEFAULT') dotted 2px;
          outline-offset: 0;
        }

        &:focus:not(:focus-visible) {
          outline: none;
        }
      }

      div[actions-card-arrow]:before {
        @apply bg-petro-p1 absolute size-16 rotate-45;

        content: '';
        right: calc(8px * sqrt(2));
      }

      div[actions-card-arrow='top']:before {
        @apply -top-8;

        border-top: inherit;
        border-left: inherit;
      }

      div[actions-card-arrow='bottom']:before {
        @apply -bottom-8;

        border-bottom: inherit;
        border-right: inherit;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableCellActionsComponent<TRowData> {
  @Input() actionsTemplate: TemplateRef<{ $implicit: TRowData }>;

  protected readonly dotsIcon = icons.ellipsis;
  protected readonly positions: Record<'top' | 'bottom', ConnectedPosition> = {
    top: {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 8,
      offsetX: 8,
    },
    bottom: {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -8,
      offsetX: 8,
    },
  };
  protected readonly overlayPositions: ConnectedPosition[] = Object.values(this.positions);
  protected readonly overlayScrollStrategy = inject(Overlay).scrollStrategies.close();

  private readonly contentSubject = new BehaviorSubject<
    TableColumnActions<TRowData>['content'] | undefined
  >(undefined);
  protected readonly content$ = this.contentSubject.asObservable();

  private readonly rowDataSubject = new BehaviorSubject<TRowData | undefined>(undefined);
  protected readonly rowData$ = this.rowDataSubject.asObservable();

  private readonly positionChangeSubject = new Subject<ConnectedOverlayPositionChange>();
  private readonly positionChange$ = this.positionChangeSubject.asObservable();

  private readonly actionSubject = new Subject<'toggle' | 'open' | 'close'>();
  protected readonly open$ = this.actionSubject.pipe(
    debounceTime(50),
    scan((isOpen, action) => {
      switch (action) {
        case 'toggle':
          return !isOpen;
        case 'open':
          return true;
        case 'close':
          return false;
      }
    }, false),
    startWith(false),
  );

  protected readonly actionsCardArrow$: Observable<'top' | 'bottom'> = this.positionChange$.pipe(
    map(({ connectionPair }) => {
      const [arrowPosition] = Object.entries(this.positions).find(([, connectedPosition]) =>
        isEqualWith(connectedPosition, connectionPair, compareConnectedPositions),
      );

      return arrowPosition as 'top' | 'bottom';
    }),
  );

  protected readonly show$: Observable<boolean> = combineLatest([
    this.content$,
    this.rowData$,
  ]).pipe(
    map(([content, rowData]) => {
      if (!content || !rowData) {
        return true;
      }

      if (typeof content.show === 'boolean') {
        return content.show;
      } else if (typeof content.show === 'function') {
        const result = content.show(rowData);

        return typeof result === 'boolean' ? result : true;
      } else if ('key' in content.show) {
        const cellData = rowData[content.show.key];

        if (typeof content.show.transform === 'function') {
          const result = content.show.transform(cellData);

          return typeof result === 'boolean' ? result : true;
        }

        return typeof cellData === 'boolean' ? cellData : true;
      }

      return true;
    }),
  );

  @Input()
  set content(value: TableColumnActions<TRowData>['content']) {
    this.contentSubject.next(value);
  }

  @Input()
  set rowData(value: TRowData) {
    this.rowDataSubject.next(value);
  }

  protected closeActionsCard(): void {
    this.actionSubject.next('close');
  }

  protected toggleActionsCard(): void {
    this.actionSubject.next('toggle');
  }

  protected onPositionChange(e: ConnectedOverlayPositionChange): void {
    this.positionChangeSubject.next(e);
  }
}
